// Ticket links: the types and the small helpers the link card and chip read. The
// API calls are methods on the api client; status words come from the ticket
// vocabulary table (ticketVocabulary.ts). Staff only: the server answers a library caller 404.

export type TicketLinkKind = 'worked_on_in' | 'duplicate_of' | 'blocks' | 'relates_to';
export type TicketLinkDirection = 'outgoing' | 'incoming';
export type { TicketKind } from './ticketVocabulary.js';
import type { TicketKind } from './ticketVocabulary.js';

export interface TicketLinkTicket {
	id: number;
	kind: TicketKind;
	number: number;
	/** Ready-made by the server ("#47512", "COM-16831"); never built from parts here. */
	ref: string;
	title: string;
	status: string;
	type: string;
	assignee_name: string;
	/** Filled only when the other ticket is a support ticket. */
	partner_name: string;
	github_pr_url: string;
	/** True when the engineering ticket is agent-driven (26 October 2026). */
	agent_driven?: boolean;
	/** Who holds it: agent, product, engineer or reporter. */
	holder_kind?: string;
}

export interface TicketLink {
	id: number;
	kind: TicketLinkKind;
	direction: TicketLinkDirection;
	done_notified_at: string | null;
	created_at: string;
	ticket: TicketLinkTicket;
}

export interface TicketLinkSummary {
	/** The row id of the engineering ticket; the chip links to it when present (contract 10a). */
	ticket_id?: number;
	ref: string;
	status: string;
	kind: TicketKind;
	link_kind: TicketLinkKind;
	/** True when the engineering ticket is agent-driven (26 October 2026). */
	agent_driven?: boolean;
	/** Who holds it: agent, product, engineer or reporter. */
	holder_kind?: string;
}

/** A ticket offered in the link dialog's search results. */
export interface TicketLinkCandidate {
	id: number;
	kind: TicketKind;
	ref: string;
	title: string;
	status: string;
	partner_name: string;
}

export const TICKET_LINK_SUMMARY_LIMIT = 200;

// ---- What the dialog offers ---------------------------------------------

export type TicketLinkChoiceId =
	| 'worked_on_in'
	| 'customer_ticket'
	| 'blocks'
	| 'blocked_by'
	| 'duplicate_of'
	| 'relates_to';

export interface TicketLinkChoice {
	id: TicketLinkChoiceId;
	label: string;
	/** The link kind stored. */
	kind: TicketLinkKind;
	/** True when the ticket picked is the "from" side and this ticket the "to" side. */
	reverse: boolean;
	/** The kinds of ticket the search looks in. */
	searchKinds: TicketKind[];
}

const CHOICES: Record<TicketLinkChoiceId, TicketLinkChoice> = {
	worked_on_in: { id: 'worked_on_in', label: 'Worked on in an engineering ticket', kind: 'worked_on_in', reverse: false, searchKinds: ['engineering'] },
	customer_ticket: { id: 'customer_ticket', label: 'A customer ticket waiting on this', kind: 'worked_on_in', reverse: true, searchKinds: ['support'] },
	blocks: { id: 'blocks', label: 'Blocks', kind: 'blocks', reverse: false, searchKinds: ['engineering'] },
	blocked_by: { id: 'blocked_by', label: 'Is blocked by', kind: 'blocks', reverse: true, searchKinds: ['engineering'] },
	duplicate_of: { id: 'duplicate_of', label: 'Duplicate of', kind: 'duplicate_of', reverse: false, searchKinds: [] },
	relates_to: { id: 'relates_to', label: 'Related to', kind: 'relates_to', reverse: false, searchKinds: ['support', 'engineering'] },
};

/** Only the link kinds the server will accept for a ticket of this kind. */
export function ticketLinkChoicesFor(ticketKind: TicketKind): TicketLinkChoice[] {
	if (ticketKind === 'engineering') {
		return [
			CHOICES.customer_ticket,
			CHOICES.blocks,
			CHOICES.blocked_by,
			{ ...CHOICES.duplicate_of, searchKinds: ['engineering'] },
			CHOICES.relates_to,
		];
	}
	return [
		CHOICES.worked_on_in,
		{ ...CHOICES.duplicate_of, searchKinds: ['support'] },
		CHOICES.relates_to,
	];
}

/** How a link reads from the ticket it is shown on. */
export function ticketLinkRelationLabel(kind: TicketLinkKind, direction: TicketLinkDirection): string {
	switch (kind) {
		case 'worked_on_in':
			return direction === 'outgoing' ? 'Worked on in' : 'Customer ticket';
		case 'blocks':
			return direction === 'outgoing' ? 'Blocks' : 'Blocked by';
		case 'duplicate_of':
			return direction === 'outgoing' ? 'Duplicate of' : 'Duplicated by';
		default:
			return 'Related to';
	}
}

/**
 * "3 libraries are waiting on this", counting each library once by name. When any
 * linked support ticket has no library name the count of libraries cannot be
 * trusted, so it says "N support tickets are waiting on this" instead. '' when none.
 */
export function librariesWaitingLine(links: TicketLink[]): string {
	const waiting = links.filter((l) => l.kind === 'worked_on_in' && l.direction === 'incoming');
	if (waiting.length === 0) return '';
	if (waiting.some((l) => !l.ticket?.partner_name)) {
		return waiting.length === 1
			? '1 support ticket is waiting on this'
			: `${waiting.length} support tickets are waiting on this`;
	}
	const n = new Set(waiting.map((l) => l.ticket?.partner_name)).size;
	return n === 1 ? '1 library is waiting on this' : `${n} libraries are waiting on this`;
}

// ---- Create an engineering ticket from a support ticket --------------------

export interface CreateEngineeringTicketBody {
	title: string;
	type: string;
	description: string;
	priority: string;
	module_id: number | null;
	assignee_id: number | null;
	move_to_with_engineering: boolean;
	/** "agent" when the reporter wants an agent to take it start to finish and will test it (refused while agent mode is off). */
	drive_mode: 'agent' | 'engineer';
}

export interface CreateEngineeringTicketResult {
	/** The new engineering ticket object (carries kind, number, ref). */
	ticket: { id: number; kind?: string; number?: number; ref?: string; title?: string; status?: string };
	/** The worked_on_in link, in the links list shape. */
	link: TicketLink;
}
